import { API } from "../../../api";
import type { Branch, Company, Contact, Deal } from "../../../models";
import { ImportOption, ImportOptionChoices } from "../../../utilities/types";

function configContains(company: Company, optionSubstring: string): boolean {
	return (
		company.portalConfigurationOptions.filter((option) => option.toLowerCase().includes(optionSubstring.toLowerCase()))
			.length > 0
	);
}

export function hasProagrica(company: Company): boolean {
	return configContains(company, "proagrica");
}

export function hasAgrian(company: Company, branches: Branch[]): boolean {
	const companyHasAgrianOrgID = (company?.externalLocationId?.length ?? 0) > 0;
	const branchHasAgrianOrgID = branches.some((branch) => (branch?.externalLocationId?.length ?? 0) > 0);
	const hasAgrianOrgID = companyHasAgrianOrgID || branchHasAgrianOrgID;

	return configContains(company, "agrian") && hasAgrianOrgID;
}

export async function getImportOptions(
	user: Contact,
	company: Company | null,
	branches: Branch[],
): Promise<ImportOption[]> {
	if (!company) {
		company = (await API.Data.Companies.get.byContact(user))[0];
	}
	const importOptions: ImportOption[] = [ImportOptionChoices.SingleUpload];

	if (user.isSuperUser || company.portalConfigurationOptions.includes("Import: CSV + SHP")) {
		importOptions.push(ImportOptionChoices.BulkUpload);
	}
	if (user.isSuperUser || company.portalConfigurationOptions.includes("Import: SHP")) {
		importOptions.push(ImportOptionChoices.BulkUpload2);
	}
	if (user.isSuperUser || hasProagrica(company)) {
		importOptions.push(ImportOptionChoices.Proagrica);
	}
	if (user.isSuperUser || hasAgrian(company, branches)) {
		importOptions.push(ImportOptionChoices.Agrian);
	}

	const uniqueOptions = [...new Set(importOptions)];
	return uniqueOptions;
}

export function sortDeals(deals: Deal[]) {
	deals.sort((dealA, dealB) => {
		return dealA.density - dealB.density;
	});
}

export function sortContacts(contacts: Contact[], branches: Branch[]): Contact[] {
	const sortedContacts = contacts.sort((a, b) => {
		// Order:
		// 1. Is Salesperson && Matches Branch
		// 2. Is Salesperson
		// 3. Remaining
		const aIsSales = a.isSalesperson;
		const bIsSales = b.isSalesperson;
		const branchIds = branches.map((b) => b.id);

		const aHasBranch =
			a.branchIds?.some((id) => branchIds.includes(id)) || a.salespersonBranchIds?.some((id) => branchIds.includes(id));
		const bHasBranch =
			b.branchIds?.some((id) => branchIds.includes(id)) || b.salespersonBranchIds?.some((id) => branchIds.includes(id));

		if (aIsSales !== bIsSales) return bIsSales ? 1 : -1;
		if (aHasBranch !== bHasBranch) return bHasBranch ? 1 : -1;
		return 0;
	});
	return sortedContacts;
}
